import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Global counts shown as pills next to "Pending Tasks" and "Reminders"
 * in the sidebar. Kept in one place so any page that mutates a task or a
 * reminder can push the sidebar to refresh without waiting for the poll.
 *
 * Exposes:
 *   - taskCount, reminderCount
 *   - refresh()  — refetch both counts from the API (safe to call after
 *                  any mutation; single call, sub-100ms in practice).
 *   - adjust({ tasks, reminders })
 *                — optimistic bump for instant UX. Server truth is fetched
 *                  right after via refresh(), so this is only a "before"
 *                  hint. Use for cases where the mutation site knows the
 *                  delta and wants zero flash.
 */
const NavCountsContext = createContext({
  taskCount: 0,
  reminderCount: 0,
  refresh: () => {},
  adjust: () => {},
});

export function NavCountsProvider({ children }) {
  const { user } = useAuth();
  const [taskCount, setTaskCount] = useState(0);
  const [reminderCount, setReminderCount] = useState(0);
  const cancelledRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [t, r] = await Promise.all([
        api.get("/tasks/counts"),
        api.get("/reminders/counts"),
      ]);
      if (cancelledRef.current) return;
      setTaskCount(t.data?.my_pending || 0);
      setReminderCount(r.data?.total || 0);
    } catch {
      // Silent — the sidebar badge is not critical; next poll will retry.
    }
  }, [user]);

  const adjust = useCallback(({ tasks = 0, reminders = 0 } = {}) => {
    if (tasks) setTaskCount((c) => Math.max(0, c + tasks));
    if (reminders) setReminderCount((c) => Math.max(0, c + reminders));
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    if (!user) {
      setTaskCount(0);
      setReminderCount(0);
      return () => { cancelledRef.current = true; };
    }
    refresh();
    // Poll every 60s so counts stay honest even without any mutations.
    const id = setInterval(refresh, 60_000);
    // Refresh on window focus — cheap, and catches renames + edits done in another tab.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, refresh]);

  return (
    <NavCountsContext.Provider value={{ taskCount, reminderCount, refresh, adjust }}>
      {children}
    </NavCountsContext.Provider>
  );
}

export function useNavCounts() {
  return useContext(NavCountsContext);
}
